/**
 * Admin Sidebar Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Navigation sidebar for the admin panel with links to all admin sections.
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Users,
  Shield,
  UsersRound,
  Settings,
  Lock,
  FileText,
  Bot,
  Mic,
  ToggleRight,
  Video,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
}

const navItems: NavItem[] = [
  {
    label: 'Users',
    path: '/admin/users',
    icon: <Users size={20} />,
    permission: 'users:read',
  },
  {
    label: 'Roles',
    path: '/admin/roles',
    icon: <Shield size={20} />,
    permission: 'roles:read',
  },
  {
    label: 'Teams',
    path: '/admin/teams',
    icon: <UsersRound size={20} />,
    permission: 'teams:read',
  },
  {
    label: 'Settings',
    path: '/admin/settings',
    icon: <Settings size={20} />,
    permission: 'settings:read',
  },
  {
    label: 'Security',
    path: '/admin/security',
    icon: <Lock size={20} />,
    permission: 'settings:read',
  },
  {
    label: 'Audit Logs',
    path: '/admin/audit',
    icon: <FileText size={20} />,
    permission: 'audit:read',
  },
  {
    label: 'AI Settings',
    path: '/admin/ai',
    icon: <Bot size={20} />,
    permission: 'settings:read',
  },
  {
    label: 'ASR / Transcription',
    path: '/admin/asr',
    icon: <Mic size={20} />,
    permission: 'settings:read',
  },
  {
    label: 'Cockpit Features',
    path: '/admin/features',
    icon: <ToggleRight size={20} />,
    permission: 'settings:read',
  },
  {
    label: 'Meeting Bots',
    path: '/admin/bots',
    icon: <Video size={20} />,
    permission: 'settings:read',
  },
];

interface AdminSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const { hasPermission, isAdmin } = useAccount();

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => {
    if (!item.permission) return true;
    // Admin has all permissions
    if (isAdmin) return true;
    return hasPermission(item.permission);
  });

  return (
    <aside
      className={`
        bg-white border-r border-gray-200 flex flex-col h-full
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-56'}
      `}
    >
      {/* Navigation */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/admin' && location.pathname.startsWith(item.path));

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors duration-150
                    ${isActive
                      ? 'bg-[#002B45] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <span className={isActive ? 'text-[#F7B500]' : ''}>
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight size={20} />
            ) : (
              <>
                <ChevronLeft size={20} />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
};

export default AdminSidebar;
